import { useMemo } from 'react';
import { range } from 'lodash/fp';
import { Select } from '@kinesis/bungle';

// The scenario's horizon: the earliest reporting year included in the
// scenario's ledger. `null` = "start at the earliest data point" (the
// unclamped default). A start-only port of suit's `ScenarioHorizonSelect` —
// community inventories have no forward projection, so there is no horizon
// end. Presentational: the value is held by the scenario editor and persisted
// on Save (like the name), so `onChange` reports the picked value; it does not
// itself persist.
//
// The picked value is the year itself. It used to be minted into an ISO date
// (`${year}-01-01`), which on a July–June scenario named a day halfway
// through the year ending June — village floored the ledger there and the
// scenario's first year loaded six months short. The year→day resolution
// belongs with the reporting boundary, which lives on the scenario in
// village; this control does not know it and must not guess it.
const CURRENT_YEAR = new Date().getFullYear();

const ScenarioHorizonSelect = (props: ScenarioHorizonSelectProps) => {
  const { value, onChange, disabled = false } = props;

  const options = useMemo(
    () => [
      { label: 'Starts at earliest data point', value: null },
      ...range(2010, CURRENT_YEAR + 1).map((year) => ({
        label: `${year}`,
        value: year,
      })),
    ],
    [],
  );

  return (
    <Select
      search
      label='Start year'
      placeholder='Select start year'
      options={options}
      value={value}
      onChange={onChange}
      disabled={disabled}
    />
  );
};

export type ScenarioHorizonSelectProps = {
  // The horizon's reporting year, or null for "earliest data point".
  value: number | null;
  onChange: (value: number | null) => void;
  disabled?: boolean;
};

export { ScenarioHorizonSelect };
